import * as THREE from 'three';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import type { WeaponSkinDefinition } from './weapon-skin-types';
import type { PBRTextureSet } from '../renderer/texture-loader';
import { applyWearShader } from './weapon-wear-shader';
import { resolveTexturePath, isSkinFeatureEnabled } from './skin-texture-lod';

// ---------------------------------------------------------------------------
// Weapon Skin Texture Loader
// ---------------------------------------------------------------------------
// Loads KTX2-compressed skin textures and applies them to weapon meshes.
// Uses IndexedDB for persistent caching (same pattern as SkinLoader) and
// an in-memory Map for loaded texture sets.
//
// Texture directory layout:
//   /textures/weapon-skins/{weaponId}/{skin-name}/color.ktx2
//   /textures/weapon-skins/{weaponId}/{skin-name}/normal.ktx2
//   /textures/weapon-skins/{weaponId}/{skin-name}/orm.ktx2
// ---------------------------------------------------------------------------

const IDB_NAME = 'counter-pump-weapon-skins';
const IDB_STORE = 'texture-cache';
const IDB_VERSION = 1;
const SKIN_TEXTURE_BASE = '/textures/weapon-skins/';

/** Check whether a mesh is a viewmodel arm/hand part that should not receive skin textures. */
function isArmPart(obj: THREE.Object3D): boolean {
  return obj.userData?.viewmodelPart === 'arm';
}

export class WeaponSkinLoader {
  private readonly ktx2Loader: KTX2Loader;
  private readonly maxAnisotropy: number;
  private readonly skinCache = new Map<number, PBRTextureSet>();
  private readonly originalMaterials = new WeakMap<THREE.Group, Map<THREE.Mesh, THREE.Material | THREE.Material[]>>();
  private db: IDBDatabase | null = null;
  private cacheAvailable = false;
  private disposed = false;

  constructor(renderer: THREE.WebGLRenderer) {
    this.maxAnisotropy = renderer?.capabilities?.getMaxAnisotropy?.() ?? 1;

    this.ktx2Loader = new KTX2Loader();
    this.ktx2Loader.setTranscoderPath('/libs/basis/');
    this.ktx2Loader.detectSupport(renderer);
  }

  // ---------------------------------------------------------------------------
  // IndexedDB Cache
  // ---------------------------------------------------------------------------

  /** Initialize IndexedDB for persistent texture caching. */
  async initCache(): Promise<void> {
    if (this.db) return;

    return new Promise<void>((resolve) => {
      const request = indexedDB.open(IDB_NAME, IDB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE);
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.cacheAvailable = true;
        resolve();
      };

      request.onerror = () => {
        this.cacheAvailable = false;
        console.warn('WeaponSkinLoader: IndexedDB init failed, proceeding without cache');
        resolve();
      };
    });
  }

  // ---------------------------------------------------------------------------
  // Texture Loading
  // ---------------------------------------------------------------------------

  /**
   * Load all KTX2 textures for a weapon skin.
   *
   * Checks the in-memory cache first, then IndexedDB, then fetches over HTTP.
   * Downloaded ArrayBuffers are cached in IndexedDB for subsequent sessions.
   *
   * @param skin - The weapon skin definition specifying texture paths.
   * @returns A PBRTextureSet with color, normal, and orm textures (nullable per-channel).
   */
  async loadSkinTextures(skin: WeaponSkinDefinition): Promise<PBRTextureSet> {
    // Return from memory cache if already loaded
    const cached = this.skinCache.get(skin.id);
    if (cached) return cached;

    // F-CM-010: route texture paths through the active quality preset so
    // mobile players load lower-resolution variants (256/512/1024) instead
    // of unconditionally hitting the desktop 1024² files. resolveTexturePath
    // is a no-op for the desktop preset (returns basePath unchanged).
    const colorPath = skin.textures?.color
      ? SKIN_TEXTURE_BASE + resolveTexturePath(skin.textures.color)
      : null;
    const normalPath = skin.textures?.normal
      ? SKIN_TEXTURE_BASE + resolveTexturePath(skin.textures.normal)
      : null;
    const ormPath = skin.textures?.orm
      ? SKIN_TEXTURE_BASE + resolveTexturePath(skin.textures.orm)
      : null;

    // Load all available maps in parallel using the resolved paths
    const [color, normal, orm] = await Promise.all([
      colorPath ? this.loadKTX2Texture(colorPath) : Promise.resolve(null),
      normalPath ? this.loadKTX2Texture(normalPath) : Promise.resolve(null),
      ormPath ? this.loadKTX2Texture(ormPath) : Promise.resolve(null),
    ]);

    // Configure color spaces
    if (color) {
      color.colorSpace = THREE.SRGBColorSpace;
    }
    if (normal) {
      normal.colorSpace = THREE.LinearSRGBColorSpace;
    }
    if (orm) {
      orm.colorSpace = THREE.LinearSRGBColorSpace;
    }

    const set: PBRTextureSet = { color, normal, orm };
    this.skinCache.set(skin.id, set);
    return set;
  }

  // ---------------------------------------------------------------------------
  // Mesh Application
  // ---------------------------------------------------------------------------

  /**
   * Apply loaded skin textures to a weapon mesh group.
   *
   * - Saves original materials for later restoration via `removeSkin()`.
   * - Skips arm/hand meshes (tagged with `viewmodelPart === 'arm'`).
   * - Upgrades MeshLambertMaterial to MeshStandardMaterial if needed.
   * - Applies PBR texture maps, tint, and material overrides from the skin definition.
   * - Applies wear shader if `wearFloat > 0.01`.
   *
   * @param weaponGroup - The THREE.Group containing the weapon meshes.
   * @param textures - The loaded PBR texture set to apply.
   * @param skin - The skin definition with material overrides.
   * @param wearFloat - Wear amount (0.0 = factory new, 1.0 = battle-scarred).
   */
  applySkinToMesh(
    weaponGroup: THREE.Group,
    textures: PBRTextureSet,
    skin: WeaponSkinDefinition,
    wearFloat: number,
  ): void {
    // Save originals if this is the first application to this group
    const firstApply = !this.originalMaterials.has(weaponGroup);
    if (firstApply) {
      const originals = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
      weaponGroup.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // Clone the material reference (not the material itself) for restoration
          originals.set(child, child.material);
        }
      });
      this.originalMaterials.set(weaponGroup, originals);
    }
    const originalsForGroup = this.originalMaterials.get(weaponGroup);

    // Apply to all mesh children
    weaponGroup.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      // Skip arm/hand meshes — they keep their own materials
      if (isArmPart(child)) return;

      // Re-apply path: dispose the prior skin material before overwriting so the
      // shader program / cloned textures don't leak. Originals are spared.
      if (!firstApply && originalsForGroup) {
        const original = originalsForGroup.get(child);
        const prior = child.material;
        if (prior && prior !== original) {
          if (Array.isArray(prior)) {
            for (const m of prior) {
              if (m !== original) m.dispose();
            }
          } else {
            prior.dispose();
          }
        }
      }

      // Get or create a MeshStandardMaterial
      let mat: THREE.MeshStandardMaterial;
      const currentMat = Array.isArray(child.material) ? child.material[0] : child.material;

      if (currentMat instanceof THREE.MeshStandardMaterial) {
        // Clone so we don't mutate shared materials
        mat = currentMat.clone();
      } else if (currentMat instanceof THREE.MeshLambertMaterial) {
        // Upgrade Lambert → Standard, preserving color
        mat = new THREE.MeshStandardMaterial({
          color: currentMat.color.clone(),
          roughness: 0.5,
          metalness: 0.0,
        });
      } else {
        // For any other material type, create a fresh Standard material
        mat = new THREE.MeshStandardMaterial({
          roughness: 0.5,
          metalness: 0.0,
        });
      }

      // Apply texture maps
      if (textures.color) {
        mat.map = textures.color;
      }
      if (textures.normal) {
        mat.normalMap = textures.normal;
        mat.normalMapType = THREE.TangentSpaceNormalMap;
      }
      if (textures.orm) {
        // ORM packing: R = AO, G = Roughness, B = Metalness
        mat.aoMap = textures.orm;
        mat.roughnessMap = textures.orm;
        mat.metalnessMap = textures.orm;
        // Ensure the mesh has a second UV set for AO. Clone the uv attribute
        // into a fresh BufferAttribute so that future edits to either UV
        // channel don't accidentally mutate the other (same-instance aliasing
        // caused surprise breakage in stickers / wear-mapping updates).
        if (child.geometry && !child.geometry.getAttribute('uv2')) {
          const uv = child.geometry.getAttribute('uv');
          if (uv && uv instanceof THREE.BufferAttribute) {
            child.geometry.setAttribute(
              'uv2',
              new THREE.BufferAttribute(
                (uv.array as Float32Array).slice(),
                uv.itemSize,
                uv.normalized,
              ),
            );
          } else if (uv) {
            // Interleaved or other attribute type — fall back to clone().
            child.geometry.setAttribute('uv2', (uv as THREE.BufferAttribute).clone());
          }
        }
      }

      // Apply skin material overrides
      if (skin.tint !== undefined) {
        mat.color.set(skin.tint);
      }
      if (skin.roughness !== undefined) {
        mat.roughness = skin.roughness;
      }
      if (skin.metalness !== undefined) {
        mat.metalness = skin.metalness;
      }

      // Emissive for glowing / custom-paint skins
      if (skin.emissiveColor !== undefined) {
        mat.emissive = new THREE.Color(skin.emissiveColor);
        mat.emissiveIntensity = skin.emissiveIntensity ?? 0;
      }

      // L21: Clamp wearFloat to valid [0, 1] range before passing to shader.
      // F-CM-010: gate the wear shader behind the active quality preset so
      // mobile-low devices skip the noise-heavy fragment chunk entirely.
      const clampedWear = Math.max(0, Math.min(1, wearFloat));
      if (isSkinFeatureEnabled('wear-shader')) {
        // Apply wear shader (skips internally if wearFloat < 0.01)
        applyWearShader(mat, clampedWear);
      }

      mat.needsUpdate = true;
      child.material = mat;
    });
  }

  // ---------------------------------------------------------------------------
  // Skin Removal
  // ---------------------------------------------------------------------------

  /**
   * Remove a skin from a weapon group, restoring original materials.
   *
   * Disposes the skin materials that were created by `applySkinToMesh`.
   * If no originals were saved (skin was never applied), this is a no-op.
   *
   * @param weaponGroup - The weapon group to restore.
   */
  removeSkin(weaponGroup: THREE.Group): void {
    const originals = this.originalMaterials.get(weaponGroup);
    if (!originals) return;

    weaponGroup.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      const original = originals.get(child);
      if (original === undefined) return;

      // Dispose the skin material we created (but NOT the textures — those are shared/cached)
      const currentMat = child.material;
      if (Array.isArray(currentMat)) {
        currentMat.forEach((m) => m.dispose());
      } else if (currentMat) {
        currentMat.dispose();
      }

      // Restore original
      child.material = original;
    });

    this.originalMaterials.delete(weaponGroup);
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  /**
   * Dispose all cached textures, close IndexedDB, and release the KTX2 transcoder.
   * After calling dispose(), this loader instance should not be reused.
   */
  dispose(): void {
    // M36: Mark as disposed so in-flight async operations can check and bail out
    this.disposed = true;

    // Dispose all cached texture sets
    for (const [, set] of this.skinCache) {
      set.color?.dispose();
      set.normal?.dispose();
      set.orm?.dispose();
    }
    this.skinCache.clear();

    // Close IndexedDB connection
    if (this.db) {
      this.db.close();
      this.db = null;
    }

    // Release KTX2 transcoder
    this.ktx2Loader.dispose();
  }

  // ---------------------------------------------------------------------------
  // Private Helpers
  // ---------------------------------------------------------------------------

  /**
   * Load a single KTX2 texture, checking IndexedDB cache first.
   * Downloads and caches on miss.
   */
  private async loadKTX2Texture(url: string): Promise<THREE.Texture> {
    // M36: Bail out if disposed during an earlier await
    if (this.disposed) {
      throw new Error('WeaponSkinLoader: loader disposed, aborting texture load');
    }

    // Try IndexedDB cache first
    const cachedBuffer = await this.idbGet(url);
    if (cachedBuffer) {
      try {
        const texture = await this.parseKTX2Buffer(cachedBuffer);
        // M36: Check disposed after async parse
        if (this.disposed) {
          texture.dispose();
          throw new Error('WeaponSkinLoader: loader disposed during KTX2 parse');
        }
        return texture;
      } catch (err) {
        if (this.disposed) throw err;
        console.warn(`WeaponSkinLoader: cached KTX2 parse failed for ${url}, fetching fresh`);
      }
    }

    // M36: Check again before network fetch
    if (this.disposed) {
      throw new Error('WeaponSkinLoader: loader disposed, aborting texture fetch');
    }

    // Fetch from network
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`WeaponSkinLoader: HTTP ${response.status} fetching ${url}`);
    }
    const buffer = await response.arrayBuffer();

    // M36: Check after network fetch completes
    if (this.disposed) {
      throw new Error('WeaponSkinLoader: loader disposed after texture download');
    }

    // Cache in background (don't block texture creation)
    this.idbPut(url, buffer).catch((err) =>
      console.warn('WeaponSkinLoader: IDB cache write failed', err),
    );

    const texture = await this.parseKTX2Buffer(buffer);
    // M36: Check disposed after parse
    if (this.disposed) {
      texture.dispose();
      throw new Error('WeaponSkinLoader: loader disposed during KTX2 parse');
    }
    return texture;
  }

  /**
   * Parse a KTX2 ArrayBuffer into a THREE.Texture using the KTX2Loader.
   */
  private parseKTX2Buffer(buffer: ArrayBuffer): Promise<THREE.Texture> {
    return new Promise<THREE.Texture>((resolve, reject) => {
      // KTX2Loader doesn't have a parseAsync by default, so we use the
      // internal _createTexture approach via a Blob URL
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      let settled = false;

      try {
        this.ktx2Loader.load(
          blobUrl,
          (texture) => {
            settled = true;
            URL.revokeObjectURL(blobUrl);
            // Configure for weapon skin use
            texture.wrapS = THREE.ClampToEdgeWrapping;
            texture.wrapT = THREE.ClampToEdgeWrapping;
            texture.minFilter = THREE.LinearMipmapLinearFilter;
            texture.generateMipmaps = false; // KTX2 includes built-in mipmaps
            texture.anisotropy = this.maxAnisotropy;
            resolve(texture);
          },
          undefined,
          (error) => {
            settled = true;
            // M25: Always revoke blob URL on error to prevent leak
            URL.revokeObjectURL(blobUrl);
            reject(error);
          },
        );
      } catch (err) {
        // M25: If KTX2Loader.load throws synchronously (e.g., transcoder not ready),
        // ensure the blob URL is still revoked
        if (!settled) {
          URL.revokeObjectURL(blobUrl);
        }
        reject(err);
      }
    });
  }

  /** Read an ArrayBuffer from IndexedDB. */
  private async idbGet(key: string): Promise<ArrayBuffer | null> {
    if (!this.db || !this.cacheAvailable) return null;
    return new Promise((resolve) => {
      try {
        const tx = this.db!.transaction(IDB_STORE, 'readonly');
        const store = tx.objectStore(IDB_STORE);
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => {
          // M26: Log specific error type instead of silently masking all errors
          const error = req.error;
          const errorName = error?.name ?? 'UnknownError';
          console.warn(`WeaponSkinLoader: IDB get failed (${errorName}) for ${key}`, error);
          resolve(null);
        };
      } catch (err) {
        // M26: Log transaction creation errors (e.g., InvalidStateError if DB is closed)
        const errorName = err instanceof DOMException ? err.name : 'UnknownError';
        console.warn(`WeaponSkinLoader: IDB transaction failed (${errorName})`, err);
        resolve(null);
      }
    });
  }

  /** Write an ArrayBuffer to IndexedDB. */
  private async idbPut(key: string, value: ArrayBuffer): Promise<void> {
    if (!this.db || !this.cacheAvailable) return;
    return new Promise((resolve, reject) => {
      try {
        const tx = this.db!.transaction(IDB_STORE, 'readwrite');
        const store = tx.objectStore(IDB_STORE);
        const req = store.put(value, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      } catch (err) {
        reject(err);
      }
    });
  }
}
